import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, ViewChild, Renderer2} from "@angular/core";
import {EmployeeService} from "../../../../../core/services/services/employee.service";
import {firstValueFrom} from "rxjs";
import {matchingEmployee, requestedSkills} from "../../employeeData.model";
import {TranslatableComponent} from "../../../../../core/models/TranslatableComponent";
import {TranslateService} from "@ngx-translate/core";
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Highcharts from "highcharts";
import {Employee} from "../../../../../core/models/Employee";
import {HighchartsChartComponent} from "../../../../../shared/highcharts-chart/highcharts-chart.component";
import {ProfilePictureComponent} from "../../../../../shared/generalized-list/profile-picture/profile-picture.component";
import {SkillDbListComponent} from "../../../../../shared/skill-db-list/components/skill-db-list.component";

@Component({
	selector: "app-employee-profile",
	templateUrl: "./employee-profile.component.html",
	styleUrls: ["./employee-profile.component.scss"],
	imports: [HighchartsChartComponent, ProfilePictureComponent, SkillDbListComponent],
})
export class EmployeeProfileComponent extends TranslatableComponent implements AfterViewInit {
	@Input() matchingEmployee: matchingEmployee;
	@Input() returnSkillLevelFromArray: (args: string[]) => number;
	@Input() requestedSkills: requestedSkills;

	@ViewChild("employeeProfileRightRow") employeeProfileRightRow!: ElementRef<HTMLDivElement>;
	@ViewChild("profilePictureContainer") profilePictureContainer!: ElementRef<HTMLDivElement>;

	private skillNames: string[] = [];
	private skillFulfillmentArray: number[] = [];

	public email: string;
	public employeeName: string;
	public employeeBU: string;
	public employeeID: number;
	public employee: Employee;

	showSkillDbTooltip = false;
	tooltipTop = 0;
	tooltipLeft = 0;
	tooltipWidth = 300;
	tooltipTimerRef: any;

	public showEvaluation: boolean;
	public skillEvaluationChartOptions: Highcharts.Options;
	public employeeProfileMatchChartOptions: Highcharts.Options;

	constructor(
		private cdr: ChangeDetectorRef,
		private renderer: Renderer2,
		private employeeService: EmployeeService,
		public _translate: TranslateService,
	) {
		super(_translate, "ChatBotComponent");
	}

	async ngAfterViewInit() {
		if (this.matchingEmployee.evaluation) {
			this.showEvaluation = true;
			this.updateEmployeeData();
			this.initializeCanvases();
		} else {
			this.showEvaluation = false;
			this.unSelectEmployee();
		}

		this.employeeService.getEmployeeByPersonnelNumber(this.matchingEmployee.id).subscribe(async (employee) => {
			if (employee) {
				this.employee = employee;
				await this.setEmployee(employee);
				this.loadEmployeeSkills(employee.id);
			}
		});
	}

	private loadEmployeeSkills(employeeId: number) {
		this.employeeService.getEmployeeSkillsById(employeeId).subscribe((skills) => {
			if (this.employee && skills) {
				this.employee.skillDbRows = skills
					.filter((skill) => skill.skill.visibility === 1)
					.map((skill) => ({
						skillNameDe: skill.skill.skillNameDe,
						skillNameEn: skill.skill.skillNameEn,
						skillNameAlt: skill.skill.skillNameAlt,
						level: skill.experienceLevel,
					}))
					.sort((a, b) => Number(b.level) - Number(a.level));

				// Calculate dynamic width based on number of skills
				const skillCount = this.employee.skillDbRows.length;
				if (skillCount <= 5) {
					this.tooltipWidth = Math.min(400, window.innerWidth * 0.9);
				} else if (skillCount <= 15) {
					this.tooltipWidth = Math.min(700, window.innerWidth * 0.9);
				} else if (skillCount <= 30) {
					this.tooltipWidth = Math.min(1000, window.innerWidth * 0.95);
				} else {
					this.tooltipWidth = Math.min(1400, window.innerWidth * 0.98);
				}

				this.cdr.detectChanges();
			}
		});
	}

	private async setEmployee(employee: Employee) {
		this.email = await firstValueFrom(this.employeeService.fetchEmployeeEmailByID(employee.id));

		let fullName = await firstValueFrom(this.employeeService.getEmployeeFullNameByEmployeeId(employee.id));

		this.employeeName = fullName.firstName + " " + fullName.lastName;

		this.employeeBU = employee.businessUnit;

		this.employeeID = employee.id;
	}

	selectEmployee() {
		this.employeeProfileRightRow.nativeElement.style.display = "flex";
	}

	unSelectEmployee() {
		this.employeeProfileRightRow.nativeElement.style.display = "none";
	}

	onMouseEnter() {
		if (this.tooltipTimerRef) {
			clearTimeout(this.tooltipTimerRef);
		}
		if (this.profilePictureContainer) {
			const rect = this.profilePictureContainer.nativeElement.getBoundingClientRect();
			let topPos = rect.top - 12;
			let leftPos = rect.left + rect.width / 2;

			// Adjust top position to prevent going out of viewport
			if (topPos < 10) {
				topPos = rect.bottom + 12; // Position below if not enough space above
			}

			// Adjust left position to keep tooltip within viewport
			const tooltipHalfWidth = this.tooltipWidth / 2;
			const padding = 16;
			if (leftPos - tooltipHalfWidth < padding) {
				leftPos = tooltipHalfWidth + padding;
			} else if (leftPos + tooltipHalfWidth > window.innerWidth - padding) {
				leftPos = window.innerWidth - tooltipHalfWidth - padding;
			}

			this.tooltipTop = topPos;
			this.tooltipLeft = leftPos;
		}
		this.showSkillDbTooltip = true;
	}

	onMouseLeave() {
		// Delay hiding to allow mouse to move over tooltip content
		this.tooltipTimerRef = setTimeout(() => {
			this.showSkillDbTooltip = false;
			this.cdr.detectChanges();
		}, 300);
	}

	onTooltipMouseEnter() {
		// Clear timeout if mouse enters tooltip to keep it visible for scrolling
		if (this.tooltipTimerRef) {
			clearTimeout(this.tooltipTimerRef);
		}
		this.showSkillDbTooltip = true;
	}

	onTooltipMouseLeave() {
		this.showSkillDbTooltip = false;
	}

	updateEmployeeData() {
		this.skillNames = Object.keys(this.matchingEmployee.evaluation?.skillFulfillments);

		this.skillFulfillmentArray = this.calculateFulfillmentLevels();
	}

	calculateFulfillmentLevels(): number[] {
		const skillFulfillments = Object.values(this.matchingEmployee.evaluation.skillFulfillments);
		const requestedSkillWeightings = this.requestedSkills.map((skill) => skill.weighting);
		const fulfillmentRatio = skillFulfillments.map((value, index) => {
			return value / requestedSkillWeightings[index];
		});

		let fulfillmentLevels: number[] = [];

		fulfillmentRatio.forEach((ratio, index) => {
			switch (true) {
				case ratio === 0:
					fulfillmentLevels[index] = 0;
					break;

				case ratio < 1:
					fulfillmentLevels[index] = 1;
					break;

				case ratio === 1:
					fulfillmentLevels[index] = 2;
					break;

				case ratio > 1:
					fulfillmentLevels[index] = 3;
					break;
			}
		});

		return fulfillmentLevels;
	}

	initializeCanvases() {
		this.employeeProfileMatchChartOptions = {
			chart: {
				type: "pie",
				backgroundColor: "rgba(0, 0, 0, 0)",
				spacing: [0, 0, 0, 0],
				margin: 0,
			},
			title: {
				text: null,
			},
			plotOptions: {
				pie: {
					size: "100%",
					innerSize: "90%",
					borderWidth: 0,
					dataLabels: {
						enabled: false,
					},
				},
			},
			series: [
				{
					name: "Fulfillment",
					data: [
						{
							name: "Fulfillment",
							y: this.matchingEmployee.evaluation.overallFulfillment / 100,
							color: "var(--mep-color-primary)",
							borderWidth: 0,
						},
						{
							name: "missing Fulfillment",
							y: 1 - this.matchingEmployee.evaluation.overallFulfillment / 100,
							color: "rgba(0, 0, 0, 0)",
							borderWidth: 0,
						},
					],
					type: "pie",
				},
			],
			legend: {
				enabled: false,
			},
			credits: {
				enabled: false,
			},
		};

		const labelTextArray = [
			this._lang("fulfillment-0"),
			this._lang("fulfillment-1"),
			this._lang("fulfillment-2"),
			this._lang("fulfillment-3"),
		];

		this.skillEvaluationChartOptions = {
			chart: {
				type: "column",
				animation: false,
				backgroundColor: "rgba(0, 0, 0, 0)",
				inverted: false,
			},
			title: {
				text: null,
			},
			xAxis: {
				categories: this.skillNames,
				title: {
					text: null,
				},
			},
			yAxis: {
				min: 0,
				max: 3,
				title: {
					text: null,
				},
				tickInterval: 1,
				labels: {
					formatter: function () {
						// Map the y-axis values to the custom labels
						return labelTextArray[this.value]; // Return the corresponding label based on the y-value
					},
				},
				plotLines: [
					{
						value: 2, // The value at which the line will appear on the yAxis
						color: "#000000", // Line color
						width: 5, // Line width
						dashStyle: "Solid", // Line style (Solid, Dash, Dot, etc.)
					},
				],
			},
			series: [
				{
					name: "Skill Fulfillment",
					data: this.skillFulfillmentArray,
					type: "column",
					color: "var(--mep-color-primary)",
					dataLabels: {
						enabled: false,
					},
				},
			],
			plotOptions: {
				column: {
					animation: false,
				},
			},
			tooltip: {
				formatter: function () {
					// Map the y-values of the data points to the custom labels
					return `${labelTextArray[this.y]}`;
				},
			},
			credits: {
				enabled: false,
			},
			legend: {
				enabled: false,
			},
		};
		this.unSelectEmployee();
	}
}
